// Teacher Slot Card Component - SAT V4 Zone
import { DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Chip, Stack, Tooltip } from '@mui/material';
import type { FC } from 'react';

export type SlotStatus = 'teaching' | 'available' | 'unavailable' | 'preferred' | 'duty' | 'other';

export interface TeacherScheduledLesson {
  periodId: string;
  dayOfWeek: string;
  week?: 'A' | 'B';
  subjectName: string;
  className: string; // e.g., "7JW", "8ST"
  setName: string; // e.g., "Set 1", "Set 2"
  subjectColor: string;
}

export interface TeacherDuty {
  periodId: string;
  dayOfWeek: string;
  week?: 'A' | 'B';
  dutyType: 'session' | 'other';
  dutyName?: string; // For 'other' type: SEND, Behavior, Attendance, Safeguarding
}

interface TeacherSlotCardProps {
  status: SlotStatus;
  lesson?: TeacherScheduledLesson;
  duty?: TeacherDuty;
  compact?: boolean;
  onClick?: () => void;
}

// Use teal/darker green for Available to differentiate from Teaching subjects
const STATUS_CONFIG: Record<Exclude<SlotStatus, 'teaching' | 'other'>, { bg: string; color: string; label: string; icon: string }> = {
  available: { bg: '#E0F2F1', color: '#00796B', label: 'Available', icon: 'CHECK_CIRCLE' },
  unavailable: { bg: '#FFEBEE', color: '#F44336', label: 'Unavailable', icon: 'CLOSE_CIRCLE' },
  preferred: { bg: '#E3F2FD', color: '#2196F3', label: 'Preferred', icon: 'STAR' },
  duty: { bg: '#FFF3E0', color: '#FF9800', label: 'On Duty', icon: 'ALERT_INFO' },
};

// Config for 'other' duties (SEND, Behavior, etc.)
const OTHER_DUTY_CONFIG = { bg: '#F3E5F5', color: '#9C27B0', icon: 'PROFILE' };

export const TeacherSlotCard: FC<TeacherSlotCardProps> = ({
  status,
  lesson,
  duty,
  compact = false,
  onClick,
}) => {
  // Teaching state - show lesson details
  if (status === 'teaching' && lesson) {
    const subjectColor = lesson.subjectColor || '#9E9E9E';

    return (
      <Box
        onClick={onClick}
        sx={{
          p: compact ? 0.75 : 1,
          borderRadius: 1,
          bgcolor: `${subjectColor}20`,
          borderLeft: 4,
          borderColor: subjectColor,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          height: '100%',
          minHeight: compact ? 40 : 70,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          '&:hover': onClick
            ? {
              boxShadow: 2,
              bgcolor: `${subjectColor}30`,
            }
            : {},
        }}
      >
        <Stack spacing={0.5}>
          <DsTypography
            variant={compact ? 'caption' : 'body2'}
            fontWeight={600}
            sx={{
              color: subjectColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {lesson.subjectName}
          </DsTypography>
          {!compact && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label={lesson.className}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: 9,
                  borderColor: subjectColor,
                  color: subjectColor,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Chip
                label={lesson.setName}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 9,
                  bgcolor: `${subjectColor}30`,
                  color: subjectColor,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Stack>
          )}
          {compact && (
            <DsTypography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {lesson.className} - {lesson.setName}
            </DsTypography>
          )}
        </Stack>
      </Box>
    );
  }

  // Other duties state (SEND, Behavior, Attendance, Safeguarding)
  if (status === 'other' && duty) {
    const config = OTHER_DUTY_CONFIG;
    const dutyLabel = duty.dutyName || 'Other';

    return (
      <Tooltip title={`${dutyLabel} - On Duty`}>
        <Box
          onClick={onClick}
          sx={{
            p: compact ? 0.5 : 1,
            borderRadius: 1,
            bgcolor: config.bg,
            borderLeft: 4,
            borderColor: config.color,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.2s',
            height: '100%',
            minHeight: compact ? 40 : 70,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': onClick
              ? {
                boxShadow: 1,
                opacity: 0.9,
              }
              : {},
          }}
        >
          <Stack spacing={0.25} alignItems="center">
            <DsTypography
              variant="caption"
              sx={{ color: config.color, fontWeight: 600, fontSize: compact ? 10 : 12 }}
            >
              {dutyLabel}
            </DsTypography>
            {!compact && (
              <DsTypography
                variant="caption"
                sx={{ color: config.color, fontWeight: 500, fontSize: 10 }}
              >
                On Duty
              </DsTypography>
            )}
          </Stack>
        </Box>
      </Tooltip>
    );
  }

  // Standard status states (available, unavailable, preferred, duty)
  const config = STATUS_CONFIG[status as Exclude<SlotStatus, 'teaching' | 'other'>];

  return (
    <Tooltip title={config.label}>
      <Box
        onClick={onClick}
        sx={{
          p: compact ? 0.5 : 1,
          borderRadius: 1,
          bgcolor: config.bg,
          borderLeft: 4,
          borderColor: config.color,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s',
          height: '100%',
          minHeight: compact ? 40 : 70,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': onClick
            ? {
              boxShadow: 1,
              opacity: 0.9,
            }
            : {},
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DsIcon icon={config.icon} sx={{ fontSize: compact ? 14 : 16, color: config.color }} />
          {!compact && (
            <DsTypography
              variant="caption"
              sx={{ color: config.color, fontWeight: 500 }}
            >
              {config.label}
            </DsTypography>
          )}
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default TeacherSlotCard;
